'use client';

import { styled } from '@linaria/react';
import MyTrackDatePicker from '@fe-news/components/molecules/MyTrackDatePicker';
import Link from 'next/link';
import Image from 'next/image';
import useRankingList from '@fe-news/hooks/use-ranking-list';
import HotPopTopicTrackingListSkeleton from './HotPopTopicTrackingListSkeleton';
import { GTMEventKey, sendGTM, CustomEventName } from '@fe-cnyes/fe-common-gtm';
import { useMyTrackDate } from '@fe-news/context/my-track-date-provider';

// type
import { RankingProductType, MyTrackSelectDate } from '@fe-news/constants/my-track/type';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 32px;
  position: relative;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: #000;
`;

const RedBar = styled.div`
  width: 5px;
  height: 22px;
  background-color: #e03f19;
  position: absolute;
  top: 7px;
  left: -25px;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  column-gap: 19px;
`;

const HotPopTopicList = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 16px;
`;

const ReadMore = styled(Link)`
  display: flex;
  font-size: 13px;
  letter-spacing: 0.5px;
  color: #e03f19;
  align-items: center;
  line-height: 1;
  font-weight: 400;

  img {
    margin-left: 4px;
  }
`;

const onClickReadMore = () => {
  sendGTM({
    [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_BUTTON,
    [GTMEventKey.SECTION]: 'News_Category_熱門排行',
    [GTMEventKey.CLICK_ITEM]: '熱門追蹤時事＿熱門時事'
  });
};

type HotPopTopicTrackingListProps = {
  isRankingList?: boolean;
  renderCard: React.ComponentType<{
    hotRank: number;
    id: string;
    index: number;
  }>;
  renderCardLazy: React.ComponentType<{
    hotRank: number;
    id: string;
    index: number;
  }>;
  children: React.ReactNode;
};

const HotPopTopicTrackingList = ({
  isRankingList,
  renderCard: RenderCard,
  renderCardLazy: RenderCardLazy,
  children
}: HotPopTopicTrackingListProps) => {
  const { selectDate } = useMyTrackDate();
  const { data: rankingList, isLoading } = useRankingList({
    type: RankingProductType.POPTOPIC
  });

  if (selectDate !== MyTrackSelectDate.RECENT_THREE_MONTH && isLoading) {
    return <HotPopTopicTrackingListSkeleton />;
  }

  return (
    <Container>
      <RedBar />
      <Title>
        <h3>熱門追蹤時事</h3>
        <TitleContainer>
          <MyTrackDatePicker />
          {isRankingList && (
            <ReadMore href='/poptopic' onClick={onClickReadMore}>
              熱門時事
              <Image src='/assets/icons/arrows/icon-arrow-right-red.png' alt='Next' width={7} height={10} />
            </ReadMore>
          )}
        </TitleContainer>
      </Title>
      {selectDate === MyTrackSelectDate.RECENT_THREE_MONTH && <> {children}</>}
      {selectDate !== MyTrackSelectDate.RECENT_THREE_MONTH && !isLoading && (
        <HotPopTopicList>
          {rankingList.map((item, index) => {
            return index < 3 ? (
              <RenderCard key={item.productId} hotRank={item.cnt} id={item.productId} index={index} />
            ) : (
              <RenderCardLazy key={item.productId} hotRank={item.cnt} id={item.productId} index={index} />
            );
          })}
        </HotPopTopicList>
      )}
    </Container>
  );
};

export default HotPopTopicTrackingList;
